import React, { useEffect, useLayoutEffect, useRef, useState } from "react";

import { mdiCheck, mdiClose, mdiTag } from "@mdi/js";
import Icon from "@mdi/react";
import classnames from "classnames";
import { createPortal } from "react-dom";

import type { ChangeEvent, JSX, MouseEvent } from "react";

import type { PictureKeys } from "../../constants/ratio";
import type {
  TagDeleteFunction,
  TagState,
  TagUpdateFunction,
} from "../../hooks/usePictureTags";
import type { PictureTag } from "../../types/common";

interface PictureTagsProps {
  tagList: PictureTag[] | null;
  onTagDelete: TagDeleteFunction;
  onTagUpdate: TagUpdateFunction;
  tagState: TagState;
  translationId: string;
  ratioId: PictureKeys;
}

function TagCount({ tagList }: { tagList: PictureTag[] }): JSX.Element {
  const tagCountContainerClassNames = classnames("flex items-end gap-2");

  return (
    <div className={tagCountContainerClassNames}>
      <span>{tagList.length}</span>
      <Icon path={mdiTag} size={0.75} />
    </div>
  );
}

function LoadingTagIcon(): JSX.Element {
  return (
    <div className="border-border border-t-primary animate-spin rounded-full border-2 p-[0.15625rem]" />
  );
}

function TagInput({
  tag,
  editingTagValue,
  setEditingTag,
  onSubmit,
  onClick,
}: {
  tag: PictureTag;
  editingTagValue?: string;
  setEditingTag: React.Dispatch<
    React.SetStateAction<{
      initialValue: string;
      value: string;
      id: string;
    } | null>
  >;
  onSubmit?: () => void;
  onClick: (event: MouseEvent) => void;
}): JSX.Element {
  const [inputWidth, setInputWidth] = useState(0);
  const spanRef = useRef<HTMLSpanElement>(null);

  const handleSpanResize = (): void => {
    const newInputWidth = spanRef.current?.offsetWidth ?? 1;
    setInputWidth(newInputWidth);
  };

  const handleChange = (
    event: ChangeEvent<HTMLInputElement>,
    id: string,
  ): void => {
    event.stopPropagation();
    const tagNewValue = event.target.value.replace(/ +(?= )/g, "");

    setEditingTag({ value: tagNewValue, id, initialValue: tag.label });
  };

  useLayoutEffect(() => {
    const spanElement = spanRef.current;

    const resizeObserver = new ResizeObserver(() => {
      handleSpanResize();
    });

    if (spanElement) {
      resizeObserver.observe(spanElement);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>,
  ): void => {
    if (event.key === "Enter") {
      event.stopPropagation();
      onSubmit?.();
    }
  };

  const inputValue = editingTagValue ?? tag.label;

  return (
    <div className="relative px-1.25" onClick={onClick}>
      <input
        maxLength={24}
        onChange={(event): void => handleChange(event, tag.uuid)}
        onKeyDown={handleKeyDown}
        style={{ width: inputWidth }}
        type="text"
        value={inputValue}
      />
      <span
        ref={spanRef}
        className="invisible absolute left-0 min-w-px whitespace-nowrap"
      >
        {`${inputValue}.`}
      </span>
    </div>
  );
}

export default function PictureTags({
  tagList,
  onTagDelete,
  onTagUpdate,
  tagState,
  translationId,
  ratioId,
}: PictureTagsProps): JSX.Element | null {
  const [editingTag, setEditingTag] = useState<{
    initialValue: string;
    value: string;
    id: string;
  } | null>(null);

  const [isOpen, setIsOpen] = useState(false);
  const tagRef = useRef<HTMLDivElement>(null);
  const portalRef = useRef<HTMLDivElement>(null);
  const tagPosition = tagRef.current?.getBoundingClientRect();

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handleClose = (event?: any): void => {
      if (isOpen && portalRef?.current?.contains(event?.target as Node)) return; // Don't close if event's target is outside the portal

      setIsOpen(false);
      setEditingTag(null);
    };

    window.addEventListener("scroll", handleClose, true);
    window.addEventListener("resize", handleClose, true);
    window.addEventListener("click", handleClose, true);

    return () => {
      window.removeEventListener("scroll", handleClose, true);
      window.removeEventListener("resize", handleClose, true);
      window.removeEventListener("click", handleClose, true);
    };
  }, [isOpen]);

  if (!tagList?.length) return null;

  const handleContainerClick = (): void => {
    setIsOpen(!isOpen);
  };

  const containerClassNames = classnames(
    "absolute left-1.25 top-1.25 cursor-pointer flex-col rounded-lg bg-overlay p-1.25 text-overlay-foreground hover:bg-overlay",
    isOpen && "p-0",
  );

  const handleDeleteTag = (event: MouseEvent, tagId: string): void => {
    event.stopPropagation();

    onTagDelete({ tagId, translationId });
  };

  const handeEditTagSubmit = (event?: MouseEvent): void => {
    event?.stopPropagation();

    if (editingTag?.value && editingTag?.value !== editingTag?.initialValue) {
      onTagUpdate({
        translationId,
        ratioId,
        tagId: editingTag.id,
        tagValue: editingTag.value.trim(),
      });
    }
  };

  const handleTagClick = (event: MouseEvent, tag: PictureTag): void => {
    event.stopPropagation();

    if (editingTag?.id !== tag.uuid) {
      setEditingTag({
        value: tag.label,
        id: tag.uuid,
        initialValue: tag.label,
      });
    }
  };

  const portalTag = createPortal(
    <div
      ref={portalRef}
      className="text-overlay-foreground fixed z-10 cursor-pointer"
      style={{
        top: tagPosition?.top,
        left: tagPosition?.left,
      }}
    >
      <div className="bg-overlay flex max-w-60 flex-row flex-wrap items-center gap-1.25 rounded-[0.3125rem] p-1.25">
        <TagCount tagList={tagList} />
        {tagList
          .sort((a, b) => a.label.localeCompare(b.label))
          .map((tag) => {
            const isTagDeleteSuccess =
              tagState?.tagId === tag.uuid && tagState?.isDeleteSuccess;

            const editingTagValue =
              editingTag?.id === tag.uuid ? editingTag.value : undefined;

            const isNotInitialValue =
              editingTag?.id === tag.uuid &&
              editingTag?.value !== editingTag?.initialValue;

            if (isTagDeleteSuccess) return null;

            return (
              <div
                key={tag.uuid}
                className="border-border-strong bg-card text-foreground flex divide-x overflow-hidden rounded-[0.3125rem] text-center text-xs"
              >
                <TagInput
                  editingTagValue={editingTagValue}
                  onClick={(event): void => handleTagClick(event, tag)}
                  setEditingTag={setEditingTag}
                  tag={tag}
                  onSubmit={
                    tagState?.isUpdateLoading ? undefined : handeEditTagSubmit
                  }
                />

                {isNotInitialValue ? (
                  <div
                    className="hover:bg-status-active/15 hover:text-status-active flex items-center px-[0.15625rem]"
                    onClick={handeEditTagSubmit}
                  >
                    {tagState?.isUpdateLoading ? (
                      <LoadingTagIcon />
                    ) : (
                      <Icon path={mdiCheck} size={0.7} />
                    )}
                  </div>
                ) : null}

                <div
                  className="hover:bg-destructive/10 hover:text-destructive flex items-center px-[0.15625rem]"
                  onClick={
                    tagState.isDeleteLoading
                      ? undefined
                      : (event): void => handleDeleteTag(event, tag.uuid)
                  }
                >
                  {tagState?.tagId === tag.uuid && tagState?.isDeleteLoading ? (
                    <LoadingTagIcon />
                  ) : (
                    <Icon path={mdiClose} size={0.7} />
                  )}
                </div>
              </div>
            );
          })}
      </div>
    </div>,
    document.body,
  );

  return (
    <div
      ref={tagRef}
      className={containerClassNames}
      onClick={handleContainerClick}
    >
      {isOpen ? portalTag : <TagCount tagList={tagList} />}
    </div>
  );
}
